"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import { AnimatePresence, motion } from "framer-motion"
import moment from "moment"
import { saveAs } from "file-saver"
import * as XLSX from "xlsx"
import { useNavigate } from "react-router-dom"
import {
  FiDownload,
  FiInfo,
  FiMoreVertical,
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiSearch,
  FiMapPin,
  FiCalendar,
  FiDollarSign,
  FiBriefcase,
  FiUsers,
  FiRefreshCw,
  FiEye,
  FiX,
} from "react-icons/fi"
import { toast } from "react-toastify"

const tamilNaduDistricts = [
  "Ariyalur",
  "Chengalpattu",
  "Chennai",
  "Coimbatore",
  "Cuddalore",
  "Dharmapuri",
  "Dindigul",
  "Erode",
  "Kallakurichi",
  "Kanchipuram",
  "Kanyakumari",
  "Karur",
  "Krishnagiri",
  "Madurai",
  "Mayiladuthurai",
  "Nagapattinam",
  "Namakkal",
  "Nilgiris",
  "Perambalur",
  "Pudukkottai",
  "Ramanathapuram",
  "Ranipet",
  "Salem",
  "Sivaganga",
  "Tenkasi",
  "Thanjavur",
  "Theni",
  "Thoothukudi",
  "Tiruchirappalli",
  "Tirunelveli",
  "Tirupathur",
  "Tiruppur",
  "Tiruvallur",
  "Tiruvannamalai",
  "Tiruvarur",
  "Vellore",
  "Viluppuram",
  "Virudhunagar",
]

const JobList = () => {
  const [jobs, setJobs] = useState([])
  const [startups, setStartups] = useState({})
  const [filters, setFilters] = useState({
    role: "",
    level: "",
    district: "",
    startDate: "",
    endDate: "",
  })
  const [grouped, setGrouped] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({})
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const [selectedStartup, setSelectedStartup] = useState(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [showStartupDetails, setShowStartupDetails] = useState(false)
  const navigate = useNavigate()

useEffect(() => {
  const fetchData = async () => {
    try {
      const jobRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs`
      );

      const startupRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/startups`
      );

      const startupMap = {};
      startupRes.data.forEach((s) => {
        startupMap[s._id] = s;
      });

      setJobs(jobRes.data);
      setStartups(startupMap);
    } catch (error) {
      // ✅ HANDLE 401 HERE
      if (error.response?.status === 401) {
        localStorage.clear();
        toast.error("Session expired. Please login again.");

        // redirect safely
        window.location.href = "/login";
      } else {
        console.error("API error:", error);
        toast.error("Something went wrong while loading data");
      }
    }
  };

  fetchData();
}, []);

  const handleDownloadExcel = () => {
    const excelData = jobs.map((job) => ({
      Startup: startups[job.userId]?.startupName || "Startup",
      Position: `${job.position} (${job.level})`,
      District: job.district,
      Salary: job.salary,
      Experience: `${job.experience} yrs`,
      PostedAt: moment(job.postedAt).format("YYYY-MM-DD"),
    }))
    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Jobs")
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    const data = new Blob([excelBuffer], { type: "application/octet-stream" })
    saveAs(data, "Jobs.xlsx")
  }

  const filteredJobs = jobs.filter((job) => {
    const postedAt = moment(job.postedAt)
    const start = filters.startDate ? moment(filters.startDate) : null
    const end = filters.endDate ? moment(filters.endDate) : null
    return (
      (!filters.role || job.role.toLowerCase().includes(filters.role.toLowerCase())) &&
      (!filters.level || job.level.toLowerCase().includes(filters.level.toLowerCase())) &&
      (!filters.district || job.district === filters.district) &&
      (!start || postedAt.isSameOrAfter(start)) &&
      (!end || postedAt.isSameOrBefore(end))
    )
  })

  const groupedByStartup = filteredJobs.reduce((acc, job) => {
    const startupName = startups[job.userId]?.startupName || "Unknown"
    acc[startupName] = acc[startupName] || []
    acc[startupName].push(job)
    return acc
  }, {})

  const handleToggleGroup = (startupName) => {
    setExpandedGroups((prev) => ({ ...prev, [startupName]: !prev[startupName] }))
  }

  const handleMenuOpen = (event) => setMenuAnchorEl(event.currentTarget)
  const handleMenuClose = () => setMenuAnchorEl(null)

  const handleGroupingToggle = () => {
    setGrouped(true)
    handleMenuClose()
  }

  const handleClearGrouping = () => {
    setGrouped(false)
    handleMenuClose()
  }

  const handleInfoClick = (event, startup) => {
    setSelectedStartup(startup)
    setShowStartupDetails(true)
  }

  const handlePopoverClose = () => {
    setShowStartupDetails(false)
    setSelectedStartup(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Background Pattern */}
      <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />

      <div className="relative z-10 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-2xl rounded-3xl p-6 mb-8"
          >
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                  <FiBriefcase className="text-white text-xl" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Startup Job Listings
                  </h1>
                  <p className="text-gray-500 text-sm">Manage and track job opportunities</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600 bg-white/50 px-4 py-2 rounded-xl border border-gray-200">
                  Total Jobs: <span className="font-semibold text-blue-600">{filteredJobs.length}</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDownloadExcel}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                >
                  <FiDownload className="text-lg" />
                  Download Excel
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <FiFilter className="text-blue-600 text-lg" />
              <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Role Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiSearch className="text-xs" />
                  Role
                </label>
                <input
                  type="text"
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                  placeholder="Search role..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
                />
              </div>

              {/* Level Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Level</label>
                <select
                  value={filters.level}
                  onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
                >
                  <option value="">All Levels</option>
                  <option value="Senior">Senior</option>
                  <option value="Intermediate">Intermediate</option>
                </select>
              </div>

              {/* District Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiMapPin className="text-xs" />
                  District
                </label>
                <select
                  value={filters.district}
                  onChange={(e) => setFilters({ ...filters, district: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
                >
                  <option value="">All Districts</option>
                  {tamilNaduDistricts.map((dist) => (
                    <option key={dist} value={dist}>
                      {dist}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiCalendar className="text-xs" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
                />
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiCalendar className="text-xs" />
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
                />
              </div>

              {/* Clear Filters */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 opacity-0">Clear</label>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    setFilters({
                      role: "",
                      level: "",
                      district: "",
                      startDate: "",
                      endDate: "",
                    })
                  }
                  className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-2 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                >
                  <FiRefreshCw className="text-sm" />
                  Clear
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FiUsers className="text-sm" />
                        Startup
                        <div className="relative">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={handleMenuOpen}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors duration-200"
                          >
                            <FiMoreVertical className="text-sm" />
                          </motion.button>

                          {/* Dropdown Menu */}
                          <AnimatePresence>
                            {menuAnchorEl && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                                className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
                              >
                                <button
                                  onClick={handleGroupingToggle}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                >
                                  Group by Startup
                                </button>
                                <button
                                  onClick={handleClearGrouping}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                >
                                  Ungroup
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Position</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">District</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Salary</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Experience</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Posted</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <AnimatePresence>
                    {(grouped ? Object.entries(groupedByStartup) : [["All", filteredJobs]]).map(
                      ([startupName, groupJobs]) => (
                        <motion.tr key={startupName}>
                          <td colSpan={8} className="p-0">
                            {grouped && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => handleToggleGroup(startupName)}
                                className="cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200"
                              >
                                <div className="flex justify-between items-center px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                                      <FiUsers className="text-white text-sm" />
                                    </div>
                                    <span className="font-semibold text-gray-900">
                                      {startupName} ({groupJobs.length} jobs)
                                    </span>
                                  </div>
                                  {expandedGroups[startupName] ? (
                                    <FiChevronUp className="text-gray-500" />
                                  ) : (
                                    <FiChevronDown className="text-gray-500" />
                                  )}
                                </div>
                              </motion.div>
                            )}

                            <AnimatePresence>
                              {(!grouped || expandedGroups[startupName]) &&
                                groupJobs.map((job, index) => (
                                  <motion.tr
                                    key={job._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    whileHover={{ backgroundColor: "#f0f9ff" }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className="hover:shadow-lg transition-all duration-300"
                                  >
                                    <td className="px-6 py-4">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl flex items-center justify-center">
                                            <FiUsers className="text-blue-600 text-sm" />
                                          </div>
                                          <div>
                                            <div className="font-medium text-gray-900">
                                              {startups[job.userId]?.startupName || "Unknown"}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                              {startups[job.userId]?.industry || "N/A"}
                                            </div>
                                          </div>
                                        </div>
                                        <motion.button
                                          whileHover={{ scale: 1.1 }}
                                          whileTap={{ scale: 0.9 }}
                                          onClick={(e) => handleInfoClick(e, startups[job.userId])}
                                          className="p-2 hover:bg-blue-100 rounded-full transition-colors duration-200"
                                        >
                                          <FiInfo className="text-blue-500 text-sm" />
                                        </motion.button>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="font-medium text-gray-900">{job.position}</div>
                                      <div className="text-sm text-gray-500">({job.level})</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {job.role}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="flex items-center gap-2 text-gray-600">
                                        <FiMapPin className="text-xs" />
                                        {job.district}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="flex items-center gap-2 font-semibold text-green-600">
                                        <FiDollarSign className="text-xs" />
                                        {job.salary}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className="text-gray-600">{job.experience} yrs</span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-600">
                                        {moment(job.postedAt).format("YYYY-MM-DD")}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => navigate(`/admin/application/${job._id}`)}
                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                                      >
                                        <FiEye className="text-xs" />
                                        View
                                      </motion.button>
                                    </td>
                                  </motion.tr>
                                ))}
                            </AnimatePresence>
                          </td>
                        </motion.tr>
                      ),
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <FiBriefcase className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No jobs found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more results</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Click outside to close menu */}
      {menuAnchorEl && <div className="fixed inset-0 z-40" onClick={handleMenuClose} />}

      {/* Startup Details Modal */}
      <AnimatePresence>
        {showStartupDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    {selectedStartup?.startupName || "Startup Details"}
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handlePopoverClose}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Full Name</label>
                    <input
                      type="text"
                      value={selectedStartup?.fullName || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Email</label>
                    <input
                      type="text"
                      value={selectedStartup?.email || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Phone</label>
                    <input
                      type="text"
                      value={selectedStartup?.phone || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Startup Name</label>
                    <input
                      type="text"
                      value={selectedStartup?.startupName || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Industry</label>
                    <input
                      type="text"
                      value={selectedStartup?.industry || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Stage</label>
                    <input
                      type="text"
                      value={selectedStartup?.stage || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Location</label>
                    <input
                      type="text"
                      value={selectedStartup?.location || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Incubation</label>
                    <input
                      type="text"
                      value={selectedStartup?.incubation || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Status</label>
                    <input
                      type="text"
                      value={selectedStartup?.status || ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Created At</label>
                    <input
                      type="text"
                      value={selectedStartup?.createdAt ? new Date(selectedStartup.createdAt).toLocaleString() : ""}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default JobList;